(() => {
  if (customElements.get('paws-series-addon')) return;

  class PawsSeriesAddon extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.index = 0;

      this.onClick = (event) => {
        const prev = event.target.closest('[data-addon-prev]');
        const next = event.target.closest('[data-addon-next]');
        if (!prev && !next) return;
        event.preventDefault();
        const slides = this.slides();
        if (slides.length <= 1) return;
        this.index = (this.index + (next ? 1 : -1) + slides.length) % slides.length;
        this.sync();
      };

      this.onSubmit = async (event) => {
        const form = event.target.closest('[data-addon-form]');
        if (!form || !this.contains(form)) return;
        event.preventDefault();

        const button = form.querySelector('button[type="submit"]');
        const status = this.querySelector('[data-addon-status]');
        if (button) button.disabled = true;
        if (status) status.textContent = '';

        try {
          const formData = new FormData(form);
          const id = formData.get('id');
          const quantity = Number(formData.get('quantity') || 1);
          const endpoint = form.action.endsWith('.js') ? form.action : form.action + '.js';

          const response = await fetch(endpoint, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ items: [{ id, quantity }] })
          });

          const payload = await response.json().catch(() => ({}));
          if (!response.ok) {
            throw new Error(payload.description || payload.message || 'Could not add this item.');
          }

          this.dataset.justAdded = 'true';
          this.hidden = true;

          let latestCart = null;
          try {
            const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
            const cartResponse = await fetch(`${root}cart.js`, {
              credentials: 'same-origin',
              cache: 'no-store',
              headers: { Accept: 'application/json' }
            });
            if (cartResponse.ok) latestCart = await cartResponse.json();
          } catch (_cartError) {
            // The normal cart update event below still refreshes the rest of the cart.
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'paws-series-addon', productVariantId: id });
          } else {
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'paws-series-addon' } }));
          }

          if (latestCart) {
            document.dispatchEvent(new CustomEvent('paws:cart-data', { detail: { cart: latestCart } }));
          }
        } catch (error) {
          if (status) status.textContent = error.message || 'Could not add this item.';
          if (button) button.disabled = false;
        }
      };

      this.addEventListener('click', this.onClick);
      this.addEventListener('submit', this.onSubmit);
      this.sync();

      this.scheduleRefresh = () => {
        if (this.dataset.justAdded === 'true') return;
        clearTimeout(this._refreshTimer);
        this._refreshTimer = setTimeout(() => this.refresh(), 120);
      };

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.source === 'paws-series-addon' && this.dataset.justAdded === 'true') return;
          this.scheduleRefresh();
        });
      } else {
        document.addEventListener('cart:updated', this.scheduleRefresh);
      }
    }

    disconnectedCallback() {
      clearTimeout(this._refreshTimer);
      this._controller?.abort();
      this.unsubscribe?.();
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('submit', this.onSubmit);
      document.removeEventListener('cart:updated', this.scheduleRefresh);
      this._bound = false;
    }

    slides() {
      return Array.from(this.querySelectorAll('[data-addon-slide]'));
    }

    sync() {
      const slides = this.slides();
      if (!slides.length) return;
      if (this.index >= slides.length) this.index = 0;

      slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.hidden = !active;
        slide.querySelectorAll('a, button, input, select').forEach((el) => {
          if (el.matches('input[type="hidden"]')) return;
          el.tabIndex = active ? 0 : -1;
        });
      });

      const nav = this.querySelector('.paws-series-addon__nav');
      if (nav) nav.hidden = slides.length <= 1;
    }

    async refresh() {
      const sectionId = this.dataset.sectionId;
      if (!sectionId || this.dataset.justAdded === 'true') return;

      this._controller?.abort();
      const controller = new AbortController();
      this._controller = controller;

      try {
        const base = this.dataset.refreshUrl || window.location.pathname;
        const url = new URL(base, window.location.origin);
        url.searchParams.set('section_id', sectionId);

        const response = await fetch(url.toString(), {
          credentials: 'same-origin',
          cache: 'no-store',
          signal: controller.signal,
          headers: { Accept: 'text/html' }
        });
        if (!response.ok) throw new Error('Add-on refresh failed');

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const updated = doc.getElementById(this.id);

        if (!updated || controller !== this._controller || !this.isConnected) {
          if (!updated) this.hidden = true;
          return;
        }

        this.innerHTML = updated.innerHTML;
        this.index = 0;
        this.hidden = false;
        this.sync();
      } catch (error) {
        if (error.name !== 'AbortError') this.hidden = true;
      }
    }
  }

  customElements.define('paws-series-addon', PawsSeriesAddon);
})();